import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { cn } from "@arcadeai/design-system/lib/utils";
import { AlertCircle, Loader2 } from "lucide-react";
import type { LogEntry } from "./use-logs-source";

type LogsContentProps = {
  logs: LogEntry[];
  isLoading: boolean;
  error: Error | null;
};

export const LogsContent = ({ logs, isLoading, error }: LogsContentProps) => {
  if (error) {
    return (
      <div className="flex h-[600px] flex-col items-center justify-center gap-3 p-8">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <p className="font-semibold text-lg">Failed to load server logs</p>
          <p className="text-muted-foreground text-sm">
            {error.message || "An unknown error occurred"}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[600px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="flex h-[600px] items-center justify-center p-8">
        <p className="text-muted-foreground text-sm">No logs available.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <table className="w-full">
        <tbody>
          {logs.map((log, index) => {
            const logKey = log.timestamp
              ? `${log.timestamp}-${log.position}`
              : `${log.position}-${index}`;
            return (
              <tr
                className="border-b last:border-b-0 hover:bg-accent/50"
                key={logKey}
              >
                <td className="w-16 shrink-0 px-4 py-2.5">
                  <Badge
                    className={cn(
                      "font-medium text-xs",
                      log.type === "info"
                        ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                        : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                    )}
                    variant="secondary"
                  >
                    {log.type.toUpperCase()}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 font-mono text-foreground/90 text-sm">
                  {log.data}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
